import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { at } from 'lodash';
import { useField } from 'formik';
import {
  FormControl,
  TextField,
  FormHelperText,
  Autocomplete,
  Chip
} from '@mui/material';

export default function MultiSelectField(props) {
  const { label, data, onChange, type, helperText, ...rest } = props;
  const [field, meta, helpers] = useField(props);
  const [touched, error] = at(meta, 'touched', 'error');
  const isError = touched && Boolean(error);
  const fieldId = props.name;
  const helperId = `${fieldId}-helper-text`;

  useEffect(() => {
    if (data?.length === 1 && field.value?.length === 0) {
      helpers.setValue([data[0]]);
      if (onChange && type === "regions") {
        onChange([data[0].id]);
      }
    }
  }, [data, field.value, helpers, onChange, type]);

  const handleChange = (event, newValue) => {
    helpers.setValue(newValue);
    if (onChange && type === "regions") {
      const ids = newValue.map(item => item.id);
      onChange(ids);
    }
  };

  const handleDelete = (valueToDelete) => {
    const newValue = field.value.filter((item) => item.id !== valueToDelete.id);
    helpers.setValue(newValue);
    if (onChange && type === "regions") {
      const ids = newValue.map(item => item.id);
      onChange(ids);
    }
  };

  return (
    <FormControl
      {...rest}
      fullWidth
      error={isError}
    >
      <Autocomplete
        id={fieldId}
        multiple
        options={data}
        getOptionLabel={(option) => option.name || ''}
        value={field.value || []}
        onChange={handleChange}
        onBlur={field.onBlur}
        disabled={data?.length === 1}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        disablePortal
        disableCloseOnSelect={false}
        componentsProps={{
          popper: {
            placement: 'bottom-start',
            modifiers: [
              {
                name: 'flip',
                enabled: false,
              },
            ],
          },
        }}
        //  Custom rendering of selected values as Chips with keyboard accessibility
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...chipProps } = getTagProps({ index });

            return (
              <Chip
                key={key}
                label={option.name}
                {...chipProps}
                tabIndex={0}
                role="button"
                aria-label={`${option.name}, selected. Press Delete, Backspace, or Enter to remove`}
                onKeyDown={(e) => {
                  // Handle keyboard deletion
                  if (
                    e.key === 'Delete' ||
                    e.key === 'Backspace' ||
                    e.key === 'Enter'
                  ) {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDelete(option);
                  }
                }}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            name={field.name}
            label={label}
            error={isError}
            aria-describedby={(helperText || isError) ? helperId : undefined}
          />
        )}
      />
      {(helperText || isError) && (
        <FormHelperText id={helperId}>
          {isError ? error : helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}

MultiSelectField.defaultProps = {
  data: []
};

MultiSelectField.propTypes = {
  data: PropTypes.array.isRequired,
  label: PropTypes.string,
  tooltip: PropTypes.string,
  onChange: PropTypes.func,
  type: PropTypes.string,
  helperText: PropTypes.string
};